"use client";

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

export function FitHeadline({
  floor = 0.72,
  className,
  children,
}: {
  floor?: number;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = ref.current;
    if (!box) return;

    let lastWidth = -1;
    const fit = () => {
      const line = box.firstElementChild as HTMLElement | null;
      if (!line) return;

      box.style.setProperty("--fit", "1");
      line.style.whiteSpace = "nowrap";

      const needed = line.scrollWidth;
      const avail = box.clientWidth;
      if (!needed || !avail) return;

      lastWidth = avail;
      const ratio = Math.min(1, avail / needed);
      if (ratio < floor) {
        box.style.setProperty("--fit", String(floor));
        line.style.whiteSpace = "";
      } else {
        box.style.setProperty("--fit", String(ratio));
      }
    };

    fit();

    const ro = new ResizeObserver((entries) => {
      if (Math.abs(entries[0].contentRect.width - lastWidth) < 0.5) return;
      fit();
    });
    ro.observe(box);

    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(fit, 100);
    };
    window.addEventListener("resize", onResize);

    document.fonts?.ready.then(fit).catch(() => {});

    return () => {
      ro.disconnect();
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [floor, children]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
